/**
 * EsploraPage.tsx — Esplora KB (CD-5): force-directed graph of synthesized wiki
 * pages + integrated chat (citations center/pulse the graph) + lint drawer
 * ("Verifica salute KB"). Live d3-force simulation with drag + zoom/pan.
 * Ported from prototype stage-kb-explore.jsx. Mock data via kbStore;
 * backend = wiki-llm-service /graph, /query (SSE), /lint.
 */
import { useAuth0 } from "@auth0/auth0-react";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationNodeDatum,
} from "d3-force";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { I } from "../../components/icons";
import { Markdown } from "../../components/Markdown";
import { ApiError, apiErrorMessage } from "../../lib/api";
import { config } from "../../lib/config";
import {
  useDeleteKbPage,
  useIsKbAdmin,
  useKbDocuments,
  useKbGraph,
  useKbLint,
  useKbPage,
  useKbQuery,
  useKbReviews,
  useRenameKbPage,
  useSaveAnalysis,
  useSaveKbPageBody,
} from "../../lib/kbApi";
import { kbStore } from "../../lib/kbStore";
import {
  MOCK_KB_DOMAINS,
  type KbLintFinding,
  type KbWikiEdge,
  type KbWikiPage,
  type LintCategory,
  type WikiPageType,
} from "../../lib/mock";
import { SynthesisProgress } from "./SynthesisProgress";

type SimNode = KbWikiPage & SimulationNodeDatum & { x: number; y: number };

type ChatCitation = { pageId: string; title: string };
type ChatMsg =
  | { role: "user"; content: string }
  | {
      role: "ai";
      content: string;
      question: string;
      citations: ChatCitation[];
      error?: boolean;
      savedId?: string;
    };

const DOMAIN_ORDER = ["HR", "IT", "Procurement", "Product", "Reference", "Project", "Altro"];

// CSS transition for PROGRAMMATIC transform moves (buttons / focus / citations).
// Wheel-zoom is driven by a requestAnimationFrame momentum glide instead (so it
// keeps coasting to a soft stop after the wheel stops); pan is direct ("none").
const TF_SMOOTH = "transform 480ms cubic-bezier(.2,.7,.2,1)";

// Stable empty references so the graph effect doesn't re-run on unrelated renders
// while the live query is still loading (TanStack returns a stable data ref).
const EMPTY_PAGES: KbWikiPage[] = [];
const EMPTY_EDGES: KbWikiEdge[] = [];
const EMPTY_LINT: KbLintFinding[] = [];

function domainColor(key: string): string {
  return MOCK_KB_DOMAINS.find((d) => d.key === key)?.color ?? "#888";
}
function nodeR(chunks: number): number {
  return Math.max(5, Math.sqrt(chunks) * 2.5);
}
function clip(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s;
}
function hexPoints(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 90);
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join(" ");
}

function TypeGlyph({
  type,
  color = "currentColor",
  size = 10,
}: {
  type: WikiPageType;
  color?: string;
  size?: number;
}): JSX.Element {
  const h = size / 2;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0, display: "block" }}>
      {type === "entity" && (
        <rect x="0.5" y="0.5" width={size - 1} height={size - 1} rx="2.5" fill={color} />
      )}
      {type === "source" && <path d={`M${h} 0L${size} ${h}L${h} ${size}L0 ${h}Z`} fill={color} />}
      {type === "analysis" && <polygon points={hexPoints(h, h, h)} fill={color} />}
      {type === "concept" && <circle cx={h} cy={h} r={h} fill={color} />}
    </svg>
  );
}

function seedNodes(pages: KbWikiPage[]): SimNode[] {
  return pages.map((p) => {
    const di = Math.max(0, DOMAIN_ORDER.indexOf(p.domain));
    const inDom = pages.filter((x) => x.domain === p.domain);
    const k = inDom.findIndex((x) => x.id === p.id);
    const angle = (di / DOMAIN_ORDER.length) * 2 * Math.PI + (k * 0.38 - 0.7);
    const radius = 220 + (k % 3) * 80;
    return {
      ...p,
      status: p.status ?? "live",
      x: 800 + Math.cos(angle) * radius,
      y: 600 + Math.sin(angle) * radius,
    };
  });
}

function Chip({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color?: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const activeBg = color || "#1E3A5F";
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        height: 28,
        padding: "0 10px",
        borderRadius: 14,
        fontSize: 11.5,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        backgroundColor: active ? activeBg : "#FAFAF8",
        color: active ? "#fff" : "#3D424E",
        border: `1px solid ${active ? activeBg : "#D6D3CC"}`,
      }}
    >
      {color && (
        <span
          style={{ width: 7, height: 7, borderRadius: "50%", background: active ? "#fff" : color }}
        />
      )}
      {label}
    </button>
  );
}

const LINT_SECTIONS: { key: LintCategory; label: string }[] = [
  { key: "contradiction", label: "Contraddizioni rilevate" },
  { key: "stale", label: "Affermazioni potenzialmente obsolete" },
  { key: "orphan", label: "Pagine orfane" },
  { key: "missing-concept", label: "Concetti senza pagina dedicata" },
  { key: "missing-link", label: "Riferimenti incrociati mancanti" },
  { key: "data-gap", label: "Lacune di dati" },
];
const LINT_RESOLVABLE = new Set<LintCategory>(["orphan", "missing-concept", "missing-link"]);

export function EsploraPage(): JSX.Element {
  const navigate = useNavigate();
  const isAdmin = useIsKbAdmin();
  const docsQuery = useKbDocuments();
  const ingestingDocs = (docsQuery.data ?? []).filter((d) => d.status === "ingesting");
  const lastIngest = (docsQuery.data ?? []).reduce((m, d) => (d.date > m ? d.date : m), "");
  const anyIngesting = ingestingDocs.length > 0;
  const graphQuery = useKbGraph({ pollWhileBusy: anyIngesting });
  const reviewsQuery = useKbReviews({ enabled: isAdmin });
  const lintQuery = useKbLint({ enabled: isAdmin });
  const kbQuery = useKbQuery();
  const saveAnalysis = useSaveAnalysis();
  const renamePage = useRenameKbPage();
  const deletePage = useDeleteKbPage();

  // When the last ingesting doc finishes, pull the graph once more so the new
  // pages appear immediately (polling stops the moment nothing is ingesting).
  const graphRefetch = graphQuery.refetch;
  const wasIngesting = useRef(false);
  useEffect(() => {
    if (wasIngesting.current && !anyIngesting) void graphRefetch();
    wasIngesting.current = anyIngesting;
  }, [anyIngesting, graphRefetch]);

  const pages = graphQuery.data?.pages ?? EMPTY_PAGES;
  // Only [[wikilink]] (ref) edges — similarity edges are not shown in the graph.
  const edges = useMemo(
    () => (graphQuery.data?.edges ?? EMPTY_EDGES).filter((e) => e.type === "ref"),
    [graphQuery.data],
  );
  const seed = useMemo(() => seedNodes(pages), [pages]);
  const analysisCount = useMemo(() => pages.filter((p) => p.type === "analysis").length, [pages]);
  const wikiCount = pages.length - analysisCount;

  // Lint resolve/ignore are client-only dismissals in v1 (no persistence
  // endpoint yet); the "new" dot clears once the drawer is opened.
  const [lintSeen, setLintSeen] = useState(false);
  const [lintDismissed, setLintDismissed] = useState<Set<string>>(() => new Set());

  // One-time Auth0 consent popup for the KB audience (localhost forces it).
  const { getAccessTokenWithPopup } = useAuth0();
  const [consenting, setConsenting] = useState(false);
  const graphTokenError = graphQuery.error instanceof ApiError && graphQuery.error.status === 401;
  async function grantKbAccess(): Promise<void> {
    setConsenting(true);
    try {
      await getAccessTokenWithPopup({ authorizationParams: { audience: config.wikiLLMAudience } });
      await graphQuery.refetch();
      if (isAdmin) await Promise.all([reviewsQuery.refetch(), lintQuery.refetch()]);
    } catch {
      // popup blocked or dismissed — the error overlay stays visible
    } finally {
      setConsenting(false);
    }
  }

  const simNodesRef = useRef<SimNode[]>(seed);
  const simRef = useRef<Simulation<SimNode, undefined> | null>(null);
  const [, setTick] = useState(0);

  const [domainFilter, setDomainFilter] = useState("Tutti");
  const [query, setQuery] = useState("");
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [rightWidth, setRightWidth] = useState(320);
  const [selected, setSelected] = useState<string | null>(null);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const [cited, setCited] = useState<string[]>([]);
  const [pulsing, setPulsing] = useState(false);
  const [tf, setTf] = useState({ k: 1, x: 0, y: 0 });
  const tfRef = useRef(tf);
  tfRef.current = tf; // mirror for the rAF zoom loop (avoids stale-closure reads)
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  // True while dragging the empty canvas → shows the grabbing-hand cursor (only
  // then; the default canvas cursor is the standard arrow, dots show a finger).
  const [panning, setPanning] = useState(false);
  // Graph + rail filter chips (rendered in the graph's top-left).
  const [typeFilter, setTypeFilter] = useState<"all" | "wiki" | "analysis">("all");
  // If the last Analisi page is deleted while its filter is active, fall back to
  // "all" (the Analisi chip greys out).
  useEffect(() => {
    if (analysisCount === 0 && typeFilter === "analysis") setTypeFilter("all");
  }, [analysisCount, typeFilter]);
  const matchesTypeFilter = (t: WikiPageType): boolean =>
    typeFilter === "all" || (typeFilter === "analysis" ? t === "analysis" : t !== "analysis");
  const [lintOpen, setLintOpen] = useState(false);
  const [lintSections, setLintSections] = useState<Record<string, boolean>>({
    contradiction: true,
  });
  const chatRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const dragRef = useRef<{ node: SimNode; moved: boolean } | null>(null);
  const panRef = useRef<{ cx: number; cy: number; tx: number; ty: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startW: number } | null>(null);
  const zoomRef = useRef<{
    targetK: number;
    anchorVB: { x: number; y: number };
    anchorLocal: { x: number; y: number };
    raf: number;
  } | null>(null);
  const [openPageId, setOpenPageId] = useState<string | null>(null);
  // The active CSS transition for the graph transform (see TF_* constants above).
  const [tfTransition, setTfTransition] = useState(TF_SMOOTH);

  useEffect(() => {
    const simNodes = seed.map((n) => ({ ...n }));
    const simEdges = edges.map((e) => ({ source: e.from, target: e.to }));
    const sim = forceSimulation<SimNode>(simNodes)
      .force(
        "link",
        forceLink<SimNode, { source: string; target: string }>(simEdges)
          .id((n) => n.id)
          .distance(190)
          .strength(0.4),
      )
      .force("charge", forceManyBody().strength(-720))
      .force("center", forceCenter(800, 600))
      .force(
        "collide",
        // extra padding leaves room for labels → far less overlap
        forceCollide<SimNode>().radius((n) => Math.sqrt(n.chunks) * 2.5 + 16),
      );
    sim.on("tick", () => setTick((t) => (t + 1) % 1e9));
    simRef.current = sim;
    simNodesRef.current = simNodes;
    setTick((t) => t + 1);
    return () => {
      sim.on("tick", null);
      sim.stop();
    };
  }, [seed, edges]);

  // Cursor-anchored wheel zoom. Each event nudges a TARGET scale by a small step
  // SCALED to the scroll intensity (so a gentle scroll = a small zoom, giving
  // fine control when homing in on a specific level); a rAF loop eases the live
  // transform toward it with light smoothing (snappy enough to land precisely,
  // not a long coast). deltaY is normalised across wheel/trackpad deltaModes.
  // Native listener (passive:false) so we can preventDefault the page scroll.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const z = {
      targetK: tfRef.current.k,
      anchorVB: { x: 0, y: 0 },
      anchorLocal: { x: 0, y: 0 },
      raf: 0,
    };
    zoomRef.current = z;
    function step(): void {
      const cur = tfRef.current;
      const k = cur.k + (z.targetK - cur.k) * 0.2; // higher = snappier, less coast
      const done = Math.abs(z.targetK - k) < 0.0015;
      const nk = done ? z.targetK : k;
      // keep the cursor-anchored point fixed: x = anchorVB - anchorLocal * k
      const next = {
        k: nk,
        x: z.anchorVB.x - z.anchorLocal.x * nk,
        y: z.anchorVB.y - z.anchorLocal.y * nk,
      };
      tfRef.current = next;
      setTf(next);
      z.raf = done ? 0 : requestAnimationFrame(step);
    }
    function onWheel(ev: WheelEvent): void {
      ev.preventDefault();
      const local = toLocal(ev.clientX, ev.clientY);
      const vb = toViewBox(ev.clientX, ev.clientY);
      if (!local || !vb) return;
      // Normalise to pixels (line/page deltaModes report tiny/huge deltas).
      let dy = ev.deltaY;
      if (ev.deltaMode === 1) dy *= 16;
      else if (ev.deltaMode === 2) dy *= 400;
      // ≤4% per event, scaled by intensity (capped so a hard fling can't leap).
      const intensity = Math.min(Math.abs(dy), 120) / 120;
      const factor = 1 + (dy < 0 ? 1 : -1) * 0.04 * intensity;
      const base = z.raf ? z.targetK : tfRef.current.k;
      z.targetK = Math.max(0.3, Math.min(4, base * factor));
      z.anchorVB = vb;
      z.anchorLocal = local;
      setTfTransition("none");
      if (!z.raf) z.raf = requestAnimationFrame(step);
    }
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      svg.removeEventListener("wheel", onWheel);
      if (z.raf) cancelAnimationFrame(z.raf);
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, thinking]);

  const displayNodes = simNodesRef.current;
  const nodeById = useMemo(
    () => Object.fromEntries(displayNodes.map((n) => [n.id, n])),
    [displayNodes],
  );
  const neighbors = useMemo(() => {
    const m: Record<string, Set<string>> = {};
    pages.forEach((n) => (m[n.id] = new Set()));
    edges.forEach((e) => {
      m[e.from]?.add(e.to);
      m[e.to]?.add(e.from);
    });
    return m;
  }, [pages, edges]);

  const passesBase = (n: SimNode): boolean => {
    if (domainFilter !== "Tutti" && n.domain !== domainFilter) return false;
    if (
      query.trim() &&
      !n.title.toLowerCase().includes(query.trim().toLowerCase()) &&
      !n.id.toLowerCase().includes(query.trim().toLowerCase())
    )
      return false;
    return true;
  };
  // Hover (or programmatic select) lights up that node + its neighbours and
  // dims the rest; chat citations fall back to the cited set.
  const focusId = selected ?? hover?.id ?? null;
  const focusSet = focusId
    ? new Set([focusId, ...(neighbors[focusId] ?? [])])
    : cited.length
      ? new Set(cited)
      : null;
  // The All / Wiki pages / Analisi chips HIDE the non-matching nodes (a true
  // filter, not a dim).
  const isHidden = (n: SimNode): boolean => !matchesTypeFilter(n.type);
  const isDim = (n: SimNode): boolean =>
    !passesBase(n) || (focusSet !== null && !focusSet.has(n.id));

  // Flat left-rail list: pages matching the active filter chip + the search box,
  // ordered by domain (no type sub-grouping).
  const railPages = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pages
      .filter((n) => matchesTypeFilter(n.type))
      .filter((n) => !q || n.title.toLowerCase().includes(q) || n.id.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => DOMAIN_ORDER.indexOf(a.domain) - DOMAIN_ORDER.indexOf(b.domain));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, query, typeFilter]);

  const chunkSum = displayNodes.reduce((s, n) => s + n.chunks, 0);
  const findings = (lintQuery.data ?? EMPTY_LINT).filter((f) => !lintDismissed.has(f.id));
  const lintCount = findings.length;
  const lintNew = !lintSeen && lintCount > 0;
  const pendingCount = reviewsQuery.data?.length ?? 0;

  function resetView(): void {
    cancelGlide();
    setTfTransition(TF_SMOOTH);
    setTf({ k: 1, x: 0, y: 0 });
    setSelected(null);
    setCited([]);
    simRef.current?.alpha(1).restart();
  }
  function send(text?: string): void {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setThinking(true);
    kbQuery.mutate(
      { question: msg, domain: domainFilter !== "Tutti" ? domainFilter : undefined },
      {
        onSuccess: (res) => {
          setThinking(false);
          setMessages((m) => [
            ...m,
            {
              role: "ai",
              content: res.answer,
              question: msg,
              citations: res.citations.map((c) => ({ pageId: c.pageId, title: c.title })),
            },
          ]);
          const ids = res.citations.map((c) => c.pageId).filter((id) => nodeById[id]);
          setCited(ids);
          setSelected(null);
          cancelGlide();
          setTfTransition(TF_SMOOTH);
          setTf({ k: 1, x: 0, y: 0 });
          if (ids.length) {
            setPulsing(true);
            setTimeout(() => setPulsing(false), 1700);
          }
        },
        onError: (err) => {
          setThinking(false);
          setMessages((m) => [
            ...m,
            {
              role: "ai",
              content: `Non sono riuscito a rispondere: ${apiErrorMessage(err)}`,
              question: msg,
              citations: [],
              error: true,
            },
          ]);
          setCited([]);
        },
      },
    );
  }

  // Save an AI answer back to the KB as an "Analisi" wiki page (Query step 5).
  function saveAsAnalysis(idx: number): void {
    const m = messages[idx];
    if (m.role !== "ai" || m.error || m.savedId || savingIdx !== null) return;
    setSavingIdx(idx);
    saveAnalysis.mutate(
      {
        question: m.question,
        answer: m.content,
        citationPageIds: m.citations.map((c) => c.pageId),
      },
      {
        onSuccess: (created) => {
          setMessages((arr) =>
            arr.map((x, i) => (i === idx && x.role === "ai" ? { ...x, savedId: created.id } : x)),
          );
        },
        onSettled: () => setSavingIdx(null),
      },
    );
  }

  // Left-rail page actions: rename (window.prompt) + delete (window.confirm).
  function onRenamePage(e: React.MouseEvent, n: KbWikiPage): void {
    e.stopPropagation();
    const next = window.prompt("Nuovo titolo della pagina:", n.title);
    if (next && next.trim() && next.trim() !== n.title) {
      renamePage.mutate({ id: n.id, title: next.trim() });
    }
  }
  function onDeletePage(e: React.MouseEvent, n: KbWikiPage): void {
    e.stopPropagation();
    if (!window.confirm(`Eliminare «${n.title}»? L'azione è permanente.`)) return;
    deletePage.mutate(n.id, {
      onSuccess: () => {
        if (openPageId === n.id) setOpenPageId(null);
        if (selected === n.id) setSelected(null);
      },
    });
  }

  // ── graph drag (d3 fx/fy) ──
  function toLocal(clientX: number, clientY: number): { x: number; y: number } | null {
    const g = gRef.current;
    const svg = svgRef.current;
    if (!g || !svg) return null;
    const m = g.getScreenCTM();
    if (!m) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }
  // Cursor position in the svg's viewBox coordinate system (pre-g-transform).
  function toViewBox(clientX: number, clientY: number): { x: number; y: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const m = svg.getScreenCTM();
    if (!m) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }
  function cancelGlide(): void {
    const z = zoomRef.current;
    if (z && z.raf) {
      cancelAnimationFrame(z.raf);
      z.raf = 0;
    }
  }
  function onDown(ev: React.PointerEvent, n: SimNode): void {
    ev.stopPropagation();
    try {
      (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    dragRef.current = { node: n, moved: false };
    n.fx = n.x;
    n.fy = n.y;
    simRef.current?.alphaTarget(0.3).restart();
  }
  function onMove(ev: React.PointerEvent): void {
    const d = dragRef.current;
    if (!d) return;
    const p = toLocal(ev.clientX, ev.clientY);
    if (p) {
      d.node.fx = p.x;
      d.node.fy = p.y;
      d.moved = true;
    }
  }
  function onUp(ev: React.PointerEvent): void {
    const d = dragRef.current;
    if (!d) return;
    if (!ev.shiftKey) {
      d.node.fx = null;
      d.node.fy = null;
    }
    simRef.current?.alphaTarget(0);
    if (!d.moved) setOpenPageId(d.node.id); // click a dot → open its synthesized page
    dragRef.current = null;
  }

  // Background pan: drag empty canvas to move the whole graph. (Node pointerdown
  // calls stopPropagation, so starting on a node drags the node instead.)
  function onBgDown(ev: React.PointerEvent): void {
    cancelGlide();
    setTfTransition("none");
    try {
      (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    panRef.current = { cx: ev.clientX, cy: ev.clientY, tx: tf.x, ty: tf.y };
    setPanning(true);
  }
  function onBgMove(ev: React.PointerEvent): void {
    const p = panRef.current;
    if (!p) return;
    const ctm = svgRef.current?.getScreenCTM();
    const ax = ctm?.a || 1;
    const ay = ctm?.d || 1;
    setTf((t) => ({
      ...t,
      x: p.tx + (ev.clientX - p.cx) / ax,
      y: p.ty + (ev.clientY - p.cy) / ay,
    }));
  }
  function onBgUp(): void {
    panRef.current = null;
    setPanning(false);
  }

  // Drag the chat panel's left edge to resize its width (clamped 300–760px).
  function onResizeDown(ev: React.PointerEvent): void {
    ev.preventDefault();
    try {
      (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    resizeRef.current = { startX: ev.clientX, startW: rightWidth };
  }
  function onResizeMove(ev: React.PointerEvent): void {
    const r = resizeRef.current;
    if (!r) return;
    setRightWidth(Math.max(300, Math.min(760, r.startW + (r.startX - ev.clientX))));
  }
  function onResizeUp(): void {
    resizeRef.current = null;
  }

  const L = leftCollapsed ? 56 : 248;
  const R = rightCollapsed ? 0 : rightWidth;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
        background: "var(--c-bg)",
        height: "100%",
      }}
    >
      <style>{`
        @keyframes kbNodePulse { 0%{stroke-width:2;} 50%{stroke-width:6;} 100%{stroke-width:2;} }
        .kb-node-pulse { animation: kbNodePulse 0.8s ease-in-out 0s 2 forwards; }
        .kb-node-cited { stroke-width: 3; filter: drop-shadow(0 0 6px currentColor); }
        .ex-dots{display:inline-flex;gap:3px}.ex-dots i{width:5px;height:5px;border-radius:50%;background:var(--c-ai);opacity:.4;animation:exBlink 1s infinite ease-in-out}
        .ex-dots i:nth-child(2){animation-delay:.15s}.ex-dots i:nth-child(3){animation-delay:.3s}
        @keyframes exBlink{0%,60%,100%{opacity:.3}30%{opacity:1}}
        @keyframes kbLintPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.5);opacity:.55}}
        .kb-lint-dot{animation:kbLintPulse 1.6s ease-in-out infinite}
        .kb-rail-row:hover{background:var(--c-bg)!important}
        .kb-rail-actions{opacity:0;transition:opacity var(--tr-fast)}
        .kb-rail-row:hover .kb-rail-actions,.kb-rail-row:focus-within .kb-rail-actions{opacity:1}
      `}</style>

      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "14px 20px",
          borderBottom: "1px solid var(--c-line)",
          background: "var(--c-bg-elev)",
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="t-eyebrow" style={{ marginBottom: 3 }}>
            Archivio · wiki
          </div>
          <h1
            style={{
              fontSize: 19,
              fontWeight: 600,
              margin: 0,
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
            }}
          >
            Esplora Knowledge Base
          </h1>
          <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 1 }}>
            Pagine wiki sintetizzate · pronte per query
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <Chip
            label="Tutti"
            active={domainFilter === "Tutti"}
            onClick={() => setDomainFilter("Tutti")}
          />
          {MOCK_KB_DOMAINS.map((d) => (
            <Chip
              key={d.key}
              label={d.label}
              color={d.color}
              active={domainFilter === d.key}
              onClick={() => setDomainFilter(d.key)}
            />
          ))}
          <button
            className="btn btn-sm"
            onClick={() => setLintOpen(true)}
            style={{ height: 28, position: "relative" }}
          >
            <I.Lint size={13} style={{ color: "var(--c-info)" }} /> Verifica salute KB
            {lintCount > 0 && (
              <span
                className="pill pill-info t-mono"
                style={{ height: 16, padding: "0 6px", fontSize: 10.5, fontWeight: 600 }}
              >
                {lintCount}
              </span>
            )}
            {lintNew && (
              <span
                className="kb-lint-dot"
                style={{
                  position: "absolute",
                  top: -3,
                  right: -3,
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "var(--c-info)",
                  border: "1.5px solid var(--c-bg-elev)",
                }}
              />
            )}
          </button>
        </div>
      </div>

      {/* Middle: left rail | graph | chat */}
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: `${L}px 1fr ${R}px`,
          minHeight: 0,
          transition: "grid-template-columns var(--tr-med)",
        }}
      >
        {/* LEFT RAIL */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            background: "var(--c-bg-elev)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: leftCollapsed ? "12px 0" : "12px 14px",
              borderBottom: "1px solid var(--c-line)",
              justifyContent: leftCollapsed ? "center" : "space-between",
            }}
          >
            {!leftCollapsed && (
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                {typeFilter === "analysis" ? "Analisi" : "Pagine wiki"}{" "}
                <span style={{ color: "var(--c-ink-3)", fontWeight: 400 }}>
                  ({railPages.length})
                </span>
              </span>
            )}
            <button
              className="btn btn-ghost btn-icon btn-sm"
              title={leftCollapsed ? "Espandi" : "Comprimi"}
              onClick={() => setLeftCollapsed((c) => !c)}
            >
              <I.Sidebar size={14} />
            </button>
          </div>
          {!leftCollapsed && (
            <div
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid var(--c-line)",
                position: "relative",
              }}
            >
              <I.Search
                size={13}
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--c-ink-4)",
                }}
              />
              <input
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca pagine wiki…"
                style={{ height: 28, fontSize: 12, paddingLeft: 26, width: "100%" }}
              />
            </div>
          )}
          <div style={{ flex: 1, overflowY: "auto", padding: leftCollapsed ? "8px 0" : "8px 8px" }}>
            {railPages.map((n) => {
              const isAnalysis = n.type === "analysis";
              if (leftCollapsed)
                return (
                  <div
                    key={n.id}
                    title={n.title}
                    style={{ display: "flex", justifyContent: "center", padding: "7px 0" }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: isAnalysis ? "var(--c-ai)" : domainColor(n.domain),
                      }}
                    />
                  </div>
                );
              return (
                <div
                  key={n.id}
                  className="kb-rail-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelected(n.id);
                    setOpenPageId(n.id); // open the synthesized page
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(n.id);
                      setOpenPageId(n.id);
                    }
                  }}
                  title={`${n.title}`}
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 8px",
                    borderRadius: 6,
                    fontSize: 12,
                    background: selected === n.id ? "var(--c-brand-soft)" : "transparent",
                    color: selected === n.id ? "var(--c-brand-ink)" : "var(--c-ink-2)",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: isAnalysis ? "var(--c-ai)" : domainColor(n.domain),
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {n.title}
                  </span>
                  <span className="kb-rail-actions" style={{ display: "flex", gap: 2 }}>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      title="Rinomina"
                      onClick={(e) => onRenamePage(e, n)}
                      style={{ height: 22, width: 22 }}
                    >
                      <I.Edit size={12} />
                    </button>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      title="Elimina"
                      onClick={(e) => onDeletePage(e, n)}
                      style={{ height: 22, width: 22, color: "var(--c-err)" }}
                    >
                      <I.Trash size={12} />
                    </button>
                  </span>
                </div>
              );
            })}
            {!leftCollapsed && railPages.length === 0 && (
              <div style={{ padding: "16px 10px", fontSize: 12, color: "var(--c-ink-4)" }}>
                Nessuna pagina.
              </div>
            )}
          </div>
          {!leftCollapsed && pendingCount > 0 && (
            <button
              onClick={() => navigate("/kb")}
              style={{
                appearance: "none",
                border: "none",
                borderTop: "1px solid var(--c-line)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "11px 14px",
                background: "var(--c-warn-soft)",
                color: "var(--c-warn)",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              <I.Alert size={14} /> <span style={{ flex: 1 }}>Pending ({pendingCount})</span>{" "}
              <I.Arrow size={13} />
            </button>
          )}
        </div>

        {/* GRAPH */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderLeft: "1px solid var(--c-line)",
            borderRight: rightCollapsed ? "none" : "1px solid var(--c-line)",
            background: "var(--c-bg)",
          }}
        >
          {/* Re-open the Chat KB panel after it's been collapsed (GAP-1 fix). */}
          {rightCollapsed && (
            <button
              className="btn btn-sm"
              title="Apri Chat KB"
              onClick={() => setRightCollapsed(false)}
              style={{
                position: "absolute",
                top: "50%",
                right: 0,
                transform: "translateY(-50%)",
                zIndex: 6,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                boxShadow: "var(--sh-1)",
              }}
            >
              <I.Comment size={13} /> Chat
            </button>
          )}
          {(graphQuery.isPending || graphQuery.isError || pages.length === 0) && (
            <GraphOverlay>
              {graphQuery.isPending ? (
                <span style={{ fontSize: 13, color: "var(--c-ink-3)" }}>
                  Caricamento del grafo…
                </span>
              ) : graphQuery.isError ? (
                <>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--c-ink-2)",
                      maxWidth: 360,
                      textAlign: "center",
                    }}
                  >
                    {graphTokenError
                      ? "Accesso alla Knowledge Base non ancora concesso."
                      : `Impossibile caricare il grafo: ${apiErrorMessage(graphQuery.error)}`}
                  </div>
                  {graphTokenError && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => void grantKbAccess()}
                      disabled={consenting}
                    >
                      {consenting ? "Apertura…" : "Concedi l'accesso alla Knowledge Base"}
                    </button>
                  )}
                </>
              ) : anyIngesting ? (
                <SynthesisProgress stage={ingestingDocs[0]?.synthesisStage} />
              ) : (
                <>
                  <div style={{ fontSize: 13, color: "var(--c-ink-2)" }}>
                    Nessuna pagina wiki ancora.
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--c-ink-3)",
                      maxWidth: 340,
                      textAlign: "center",
                    }}
                  >
                    Carica un documento nella Knowledge Base: verrà sintetizzato in pagine wiki
                    collegate che appariranno qui.
                  </div>
                  <button className="btn btn-sm" onClick={() => navigate("/kb")}>
                    Vai alla Knowledge Base
                  </button>
                </>
              )}
            </GraphOverlay>
          )}
          <svg
            ref={svgRef}
            viewBox="0 0 1600 1200"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{
              display: "block",
              touchAction: "none",
              cursor: panning ? "grabbing" : "default",
            }}
            onClick={() => setSelected(null)}
            onPointerDown={onBgDown}
            onPointerMove={onBgMove}
            onPointerUp={onBgUp}
            onPointerCancel={onBgUp}
          >
            <defs>
              <pattern id="exDots" width="26" height="26" patternUnits="userSpaceOnUse">
                <circle cx="1.2" cy="1.2" r="1.2" fill="rgba(20,22,28,0.05)" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="1600" height="1200" fill="url(#exDots)" />
            <g
              ref={gRef}
              transform={`translate(${tf.x},${tf.y}) scale(${tf.k})`}
              style={{ transition: tfTransition }}
            >
              {edges.map((e, i) => {
                const a = nodeById[e.from];
                const b = nodeById[e.to];
                if (!a || !b) return null;
                if (isHidden(a) || isHidden(b)) return null; // filter chip hides these
                const lit = !isDim(a) && !isDim(b);
                return (
                  <line
                    key={i}
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    stroke="var(--c-ink-4)"
                    strokeWidth={0.4}
                    strokeOpacity={lit ? 0.55 : 0.07}
                    style={{ transition: "stroke-opacity 220ms" }}
                  />
                );
              })}
              {displayNodes.map((n) => {
                if (isHidden(n)) return null; // filter chip (All / Wiki pages / Analisi)
                const dim = isDim(n);
                const r = nodeR(n.chunks);
                const col = n.type === "analysis" ? "var(--c-ai)" : domainColor(n.domain);
                const isSel = selected === n.id;
                const isCited = cited.includes(n.id);
                const cls =
                  `${isSel || isCited ? "kb-node-cited" : ""} ${pulsing && isCited ? "kb-node-pulse" : ""}`.trim();
                return (
                  <g
                    key={n.id}
                    style={{
                      opacity: dim ? 0.15 : n.status === "rejected" ? 0.4 : 1,
                      transition: "opacity 220ms",
                      cursor: "pointer",
                      touchAction: "none",
                    }}
                    onPointerDown={(ev) => onDown(ev, n)}
                    onPointerMove={onMove}
                    onPointerUp={onUp}
                    onPointerCancel={onUp}
                    onClick={(ev) => ev.stopPropagation()}
                    onMouseEnter={(ev) => setHover({ id: n.id, x: ev.clientX, y: ev.clientY })}
                    onMouseMove={(ev) => {
                      if (!dragRef.current) setHover({ id: n.id, x: ev.clientX, y: ev.clientY });
                    }}
                    onMouseLeave={() => {
                      if (!dragRef.current) setHover(null);
                    }}
                  >
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r={r}
                      fill={col}
                      className={cls}
                      style={{ color: col }}
                      stroke={isSel || isCited || n.status === "pending-review" ? col : "none"}
                      strokeWidth={n.status === "pending-review" ? 1.6 : 0}
                      strokeDasharray={n.status === "pending-review" ? "3,3" : undefined}
                    />
                    <text
                      x={n.x}
                      y={n.y + r + 13}
                      textAnchor="middle"
                      fontSize="11.5"
                      fontFamily="var(--font-elegant)"
                      fontWeight={300}
                      fill="var(--c-ink-2)"
                      style={{
                        pointerEvents: "none",
                        userSelect: "none",
                        paintOrder: "stroke",
                        stroke: "var(--c-bg)",
                        strokeWidth: 1,
                        strokeLinejoin: "round",
                      }}
                    >
                      {clip(n.title, 26)}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>

          {/* controls */}
          <div
            style={{
              position: "absolute",
              left: 12,
              bottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                background: "var(--c-bg-elev)",
                border: "1px solid var(--c-line)",
                borderRadius: 8,
                boxShadow: "var(--sh-1)",
              }}
            >
              <button
                className="btn btn-ghost btn-icon btn-sm"
                title="Zoom +"
                onClick={() => {
                  cancelGlide();
                  setTfTransition(TF_SMOOTH);
                  setTf((t) => ({
                    ...t,
                    k: Math.min(4, t.k * 1.2),
                    x: 800 - (800 - t.x) * 1.2,
                    y: 600 - (600 - t.y) * 1.2,
                  }));
                }}
              >
                <I.Plus size={13} />
              </button>
              <div style={{ width: 1, background: "var(--c-line)" }} />
              <button
                className="btn btn-ghost btn-icon btn-sm"
                title="Zoom −"
                onClick={() => {
                  cancelGlide();
                  setTfTransition(TF_SMOOTH);
                  setTf((t) => ({
                    ...t,
                    k: Math.max(0.3, t.k / 1.2),
                    x: 800 - (800 - t.x) / 1.2,
                    y: 600 - (600 - t.y) / 1.2,
                  }));
                }}
              >
                <I.Minus size={13} />
              </button>
            </div>
            <span
              className="t-mono"
              style={{
                fontSize: 11,
                color: "var(--c-ink-3)",
                background: "var(--c-bg-elev)",
                border: "1px solid var(--c-line)",
                borderRadius: 6,
                padding: "3px 7px",
              }}
            >
              {Math.round(tf.k * 100)}%
            </span>
            <button className="btn btn-sm" onClick={resetView}>
              <I.Refresh size={12} /> Reset vista
            </button>
          </div>

          {/* filter chips — All / Wiki pages / Analisi (top-left, over the dots) */}
          <div
            style={{
              position: "absolute",
              left: 12,
              top: 12,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {(
              [
                { key: "all", label: "All", count: pages.length, dot: null },
                { key: "wiki", label: "Wiki pages", count: wikiCount, dot: null },
                { key: "analysis", label: "Analisi", count: analysisCount, dot: "var(--c-ai)" },
              ] as const
            ).map((c) => {
              const active = typeFilter === c.key;
              const disabled = c.key === "analysis" && analysisCount === 0;
              return (
                <button
                  key={c.key}
                  disabled={disabled}
                  aria-pressed={active}
                  title={
                    disabled ? "Nessuna analisi salvata ancora" : `Mostra: ${c.label.toLowerCase()}`
                  }
                  onClick={() => setTypeFilter(c.key)}
                  style={{
                    appearance: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    fontSize: 10.5,
                    fontWeight: active ? 600 : 500,
                    padding: "5px 10px",
                    borderRadius: 8,
                    boxShadow: "var(--sh-1)",
                    border: active ? "1px solid var(--c-brand)" : "1px solid var(--c-line)",
                    background: active ? "var(--c-brand-soft)" : "var(--c-bg-elev)",
                    color: active ? "var(--c-brand-ink)" : "var(--c-ink-3)",
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  {c.dot && (
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.dot }} />
                  )}
                  {c.label}
                  <span style={{ fontWeight: 400, color: "var(--c-ink-4)" }}>{c.count}</span>
                </button>
              );
            })}
          </div>

          {/* tooltip */}
          {hover && nodeById[hover.id] && (
            <div
              style={{
                position: "fixed",
                left: Math.min(hover.x + 14, window.innerWidth - 280),
                top: hover.y + 14,
                zIndex: 60,
                width: 260,
                background: "var(--c-bg-elev)",
                border: "1px solid var(--c-line-strong)",
                borderRadius: 10,
                boxShadow: "var(--sh-pop)",
                padding: 12,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  marginBottom: 6,
                  flexWrap: "wrap",
                }}
              >
                <TypeGlyph
                  type={nodeById[hover.id].type}
                  color={domainColor(nodeById[hover.id].domain)}
                  size={9}
                />
                <span className="t-mono" style={{ fontSize: 10.5, color: "var(--c-ink-3)" }}>
                  {hover.id}
                </span>
                <span
                  className="pill"
                  style={{
                    height: 16,
                    padding: "0 6px",
                    fontSize: 9.5,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {nodeById[hover.id].type}
                </span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, marginBottom: 6 }}>
                {nodeById[hover.id].title}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
                Dominio:{" "}
                <strong style={{ color: "var(--c-ink-2)", fontWeight: 500 }}>
                  {kbStore.domain(nodeById[hover.id].domain)?.label ?? nodeById[hover.id].domain}
                </strong>{" "}
                · Chunk:{" "}
                <strong className="t-mono" style={{ color: "var(--c-ink-2)", fontWeight: 500 }}>
                  {nodeById[hover.id].chunks}
                </strong>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT CHAT */}
        {!rightCollapsed && (
          <aside
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              background: "var(--c-bg-elev)",
              borderLeft: "1px solid var(--c-line)",
              position: "relative",
            }}
          >
            <div
              onPointerDown={onResizeDown}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeUp}
              onPointerCancel={onResizeUp}
              title="Trascina per ridimensionare"
              style={{
                position: "absolute",
                left: -3,
                top: 0,
                bottom: 0,
                width: 6,
                cursor: "col-resize",
                zIndex: 5,
                touchAction: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                borderBottom: "1px solid var(--c-line)",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: "var(--c-ai-soft)",
                  color: "var(--c-ai)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <I.Comment size={13} />
              </div>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>Chat KB</div>
              <button
                className="btn btn-ghost btn-icon btn-sm"
                title="Comprimi"
                onClick={() => setRightCollapsed(true)}
              >
                <I.X size={13} />
              </button>
            </div>
            <div
              ref={chatRef}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: 14,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {messages.length === 0 && !thinking && (
                <div
                  style={{
                    margin: "auto 0",
                    textAlign: "center",
                    color: "var(--c-ink-3)",
                    padding: "20px 6px",
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "var(--c-ai-soft)",
                      color: "var(--c-ai)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 12,
                    }}
                  >
                    <I.Sparkle size={18} />
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--c-ink-2)",
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                  >
                    Chiedi qualsiasi cosa sulla conoscenza aziendale.
                  </div>
                </div>
              )}
              {messages.map((m, i) =>
                m.role === "user" ? (
                  <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div
                      style={{
                        maxWidth: "88%",
                        padding: "8px 11px",
                        background: "var(--c-brand)",
                        color: "var(--c-ink-inv)",
                        borderRadius: "12px 12px 2px 12px",
                        fontSize: 12.5,
                        lineHeight: 1.5,
                      }}
                    >
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <span className="ai-badge" style={{ alignSelf: "flex-start" }}>
                      <I.Sparkle size={9} /> AI
                    </span>
                    <div style={{ color: "var(--c-ink)" }}>
                      <Markdown variant="chat">{m.content}</Markdown>
                    </div>
                    {!m.error &&
                      (m.savedId ? (
                        <button
                          onClick={() => setOpenPageId(m.savedId ?? null)}
                          title="Apri la pagina Analisi salvata"
                          style={{
                            alignSelf: "flex-start",
                            appearance: "none",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontFamily: "inherit",
                            fontSize: 11,
                            fontWeight: 600,
                            background: "var(--c-ai-soft)",
                            color: "var(--c-ai)",
                          }}
                        >
                          <I.Check size={11} /> Salvata come Analisi · apri
                        </button>
                      ) : (
                        <button
                          onClick={() => saveAsAnalysis(i)}
                          disabled={savingIdx !== null}
                          title="Salva questa risposta come pagina wiki (Analisi)"
                          style={{
                            alignSelf: "flex-start",
                            appearance: "none",
                            cursor: savingIdx !== null ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontFamily: "inherit",
                            fontSize: 11,
                            fontWeight: 600,
                            border: "1px solid var(--c-line-strong)",
                            background: "var(--c-bg-elev)",
                            color: "var(--c-ink-2)",
                            opacity: savingIdx !== null && savingIdx !== i ? 0.5 : 1,
                          }}
                        >
                          {savingIdx === i ? (
                            "Salvataggio…"
                          ) : (
                            <>
                              <I.Plus size={11} /> Salva come Analisi
                              {m.citations.length > 0 ? ` · cita ${m.citations.length}` : ""}
                            </>
                          )}
                        </button>
                      ))}
                  </div>
                ),
              )}
              {thinking && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "var(--c-ink-3)",
                    fontSize: 12,
                  }}
                >
                  <span className="ai-badge">
                    <I.Sparkle size={9} /> AI
                  </span>
                  <span className="ex-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              )}
            </div>
            <div style={{ padding: "10px 14px 14px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 6,
                  padding: 8,
                  border: "1px solid var(--c-line-strong)",
                  borderRadius: 10,
                  background: "var(--c-bg)",
                }}
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Chiedi qualcosa sulla KB…"
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    resize: "none",
                    fontFamily: "inherit",
                    fontSize: 12.5,
                    color: "var(--c-ink)",
                    padding: "4px",
                    lineHeight: 1.4,
                    maxHeight: 90,
                  }}
                />
                <button
                  className="btn btn-primary btn-sm btn-icon"
                  onClick={() => send()}
                  disabled={!input.trim()}
                  style={{ opacity: input.trim() ? 1 : 0.5 }}
                >
                  <I.Send size={13} />
                </button>
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* Bottom stat bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "9px 20px",
          borderTop: "1px solid var(--c-line)",
          background: "var(--c-bg-elev)",
          fontSize: 12,
          color: "var(--c-ink-3)",
          flexShrink: 0,
        }}
      >
        <I.Layers size={13} style={{ color: "var(--c-ink-4)" }} />
        <span className="t-num">
          <strong style={{ color: "var(--c-ink-2)", fontWeight: 600 }}>{pages.length}</strong>{" "}
          pagine wiki ·{" "}
          <strong style={{ color: "var(--c-ink-2)", fontWeight: 600 }}>{chunkSum}</strong> chunk ·{" "}
          <strong style={{ color: "var(--c-ink-2)", fontWeight: 600 }}>{edges.length}</strong>{" "}
          connessioni{lastIngest ? ` · ultimo ingest ${lastIngest}` : ""}
        </span>
      </div>

      {openPageId && <PageReadingView pageId={openPageId} onClose={() => setOpenPageId(null)} />}
      {lintOpen && (
        <LintDrawer
          findings={findings}
          onClose={() => {
            setLintOpen(false);
            setLintSeen(true);
          }}
          sections={lintSections}
          toggle={(k) => setLintSections((o) => ({ ...o, [k]: !o[k] }))}
          onGoReview={() => navigate("/kb")}
          onGoPages={(f) => {
            setLintOpen(false);
            const ps = [f.pageA, f.pageB]
              .filter(Boolean)
              .filter((id) => id && nodeById[id]) as string[];
            if (ps.length === 1) {
              setSelected(ps[0]);
              setCited([]);
            } else if (ps.length > 1) {
              setSelected(null);
              setCited(ps);
              setPulsing(true);
              setTimeout(() => setPulsing(false), 1700);
            }
          }}
          onResolve={(f) => setLintDismissed((s) => new Set(s).add(f.id))}
          onIgnore={(f) => setLintDismissed((s) => new Set(s).add(f.id))}
        />
      )}
    </div>
  );
}

// ─── Page reading view (synthesized .md, rendered) ───────────────────────────
function stripLeadingH1(md: string): string {
  if (md.startsWith("# ")) {
    const nl = md.indexOf("\n");
    return nl === -1 ? "" : md.slice(nl + 1).replace(/^\n+/, "");
  }
  return md;
}

function PropertyRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <span style={{ color: "var(--c-ink-3)" }}>{label}</span>
      <span style={{ color: "var(--c-ink-2)" }}>{value}</span>
    </>
  );
}

function PageReadingView({
  pageId,
  onClose,
}: {
  pageId: string;
  onClose: () => void;
}): JSX.Element {
  const { data, isPending, isError, error } = useKbPage(pageId);
  const saveBody = useSaveKbPageBody();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const props = data?.properties ?? {};
  const tags = Array.isArray(props.tags) ? (props.tags as string[]) : [];

  function startEdit(): void {
    if (!data) return;
    setDraft(stripLeadingH1(data.bodyMarkdown));
    setEditing(true);
  }
  function saveEdit(): void {
    saveBody.mutate({ id: pageId, bodyMarkdown: draft }, { onSuccess: () => setEditing(false) });
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "var(--c-bg-overlay)",
        display: "flex",
        justifyContent: "center",
        overflowY: "auto",
        padding: "40px 20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        style={{
          width: 760,
          maxWidth: "100%",
          alignSelf: "flex-start",
          background: "var(--c-bg-elev)",
          border: "1px solid var(--c-line)",
          borderRadius: 12,
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          minHeight: 160,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 20px",
            borderBottom: "1px solid var(--c-line)",
            position: "sticky",
            top: 0,
            background: "var(--c-bg-elev)",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <I.Layers size={14} style={{ color: "var(--c-ink-4)" }} />
          <span className="t-eyebrow" style={{ flex: 1 }}>
            Pagina wiki · sintetizzata
          </span>
          {data && !editing && (
            <button className="btn btn-ghost btn-sm" onClick={startEdit} style={{ height: 28 }}>
              <I.Edit size={13} /> Modifica
            </button>
          )}
          {data && editing && (
            <>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setEditing(false)}
                disabled={saveBody.isPending}
                style={{ height: 28 }}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={saveEdit}
                disabled={saveBody.isPending || !draft.trim()}
                style={{ height: 28 }}
              >
                <I.Check size={13} /> {saveBody.isPending ? "Salvataggio…" : "Salva"}
              </button>
            </>
          )}
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>
        <div style={{ padding: "22px 30px 44px" }}>
          {isPending && (
            <div style={{ fontSize: 13, color: "var(--c-ink-3)" }}>Caricamento della pagina…</div>
          )}
          {isError && (
            <div style={{ fontSize: 13, color: "var(--c-warn)" }}>
              Impossibile caricare la pagina: {apiErrorMessage(error)}
            </div>
          )}
          {data && (
            <>
              <h1
                style={{
                  fontSize: 25,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  margin: "0 0 14px",
                }}
              >
                {data.title}
              </h1>
              <div
                style={{
                  marginBottom: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--c-ink-4)",
                }}
              >
                Properties
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "120px 1fr",
                  gap: "6px 16px",
                  fontSize: 12.5,
                }}
              >
                <PropertyRow label="type" value={data.type} />
                {data.domain && <PropertyRow label="domain" value={data.domain} />}
                {typeof props.created === "string" && (
                  <PropertyRow label="created" value={props.created} />
                )}
                {typeof props.updated === "string" && (
                  <PropertyRow label="updated" value={props.updated} />
                )}
                {tags.length > 0 && <PropertyRow label="tags" value={tags.join(", ")} />}
                {data.sources.length > 0 && (
                  <>
                    <span style={{ color: "var(--c-ink-3)" }}>sources</span>
                    <span style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                      {data.sources.map((s) => (
                        <a
                          key={s.documentId}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--c-brand, #1e3a5f)",
                            textDecoration: "underline",
                            textUnderlineOffset: 2,
                          }}
                        >
                          {s.filename}
                        </a>
                      ))}
                    </span>
                  </>
                )}
              </div>
              <hr
                style={{ border: "none", borderTop: "1px solid var(--c-line)", margin: "18px 0" }}
              />
              {editing ? (
                <>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    spellCheck={false}
                    style={{
                      width: "100%",
                      minHeight: 360,
                      resize: "vertical",
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: "1px solid var(--c-line-strong)",
                      background: "var(--c-bg)",
                      color: "var(--c-ink)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12.5,
                      lineHeight: 1.6,
                      outline: "none",
                    }}
                  />
                  <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--c-ink-4)" }}>
                    Markdown supportato. Il titolo si modifica dalla lista a sinistra (Rinomina).
                  </div>
                  {saveBody.isError && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--c-err)" }}>
                      Salvataggio non riuscito: {apiErrorMessage(saveBody.error)}
                    </div>
                  )}
                </>
              ) : (
                <Markdown>{stripLeadingH1(data.bodyMarkdown)}</Markdown>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Graph state overlay (loading / error / empty) ───────────────────────────
function GraphOverlay({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        background: "var(--c-bg)",
      }}
    >
      {children}
    </div>
  );
}

// ─── Lint drawer ─────────────────────────────────────────────────────────────
function LintDrawer({
  findings,
  onClose,
  sections,
  toggle,
  onGoReview,
  onGoPages,
  onResolve,
  onIgnore,
}: {
  findings: KbLintFinding[];
  onClose: () => void;
  sections: Record<string, boolean>;
  toggle: (k: string) => void;
  onGoReview: (f: KbLintFinding) => void;
  onGoPages: (f: KbLintFinding) => void;
  onResolve: (f: KbLintFinding) => void;
  onIgnore: (f: KbLintFinding) => void;
}): JSX.Element {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 55,
        background: "var(--c-bg-overlay)",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        className="fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: "100%",
          height: "100%",
          background: "var(--c-bg-elev)",
          borderLeft: "1px solid var(--c-line)",
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "var(--c-info-soft)",
              color: "var(--c-info)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <I.Lint size={15} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Verifica salute KB</div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>Lint automatico</div>
          </div>
          <span className="pill pill-info t-mono" style={{ fontWeight: 600 }}>
            {findings.length}
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px 24px" }}>
          {findings.length === 0 && (
            <div style={{ padding: "48px 20px", textAlign: "center" }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "var(--c-ok-soft)",
                  color: "var(--c-ok)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <I.CheckC size={20} />
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
                Nessun finding attivo
              </div>
              <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>
                La Knowledge Base è in salute.
              </div>
            </div>
          )}
          {LINT_SECTIONS.map((sec) => {
            const items = findings.filter((f) => f.category === sec.key);
            if (!items.length) return null;
            const isOpen = !!sections[sec.key];
            const hasWarn = items.some((f) => f.severity === "warn");
            return (
              <div key={sec.key} style={{ marginTop: 8 }}>
                <button
                  onClick={() => toggle(sec.key)}
                  style={{
                    appearance: "none",
                    border: "1px solid var(--c-line)",
                    cursor: "pointer",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "9px 12px",
                    borderRadius: isOpen ? "9px 9px 0 0" : 9,
                    background: "var(--c-bg)",
                    fontFamily: "inherit",
                    textAlign: "left",
                  }}
                >
                  {hasWarn ? (
                    <I.Alert size={13} style={{ color: "var(--c-warn)", flexShrink: 0 }} />
                  ) : (
                    <I.Info size={13} style={{ color: "var(--c-info)", flexShrink: 0 }} />
                  )}
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: "var(--c-ink)" }}>
                    {sec.label}
                  </span>
                  <span
                    className="pill t-mono"
                    style={{ height: 17, padding: "0 6px", fontSize: 10.5 }}
                  >
                    {items.length}
                  </span>
                  {isOpen ? (
                    <I.ChevronU size={13} style={{ color: "var(--c-ink-3)" }} />
                  ) : (
                    <I.ChevronD size={13} style={{ color: "var(--c-ink-3)" }} />
                  )}
                </button>
                {isOpen && (
                  <div
                    style={{
                      border: "1px solid var(--c-line)",
                      borderTop: "none",
                      borderRadius: "0 0 9px 9px",
                      padding: 10,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      background: "var(--c-bg-sunken)",
                    }}
                  >
                    {items.map((f) => (
                      <div key={f.id} className="card" style={{ padding: 12, boxShadow: "none" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginBottom: 7,
                          }}
                        >
                          {f.severity === "warn" ? (
                            <I.Alert
                              size={14}
                              style={{ color: "var(--c-warn)", flexShrink: 0, marginTop: 1 }}
                            />
                          ) : (
                            <I.Info
                              size={14}
                              style={{ color: "var(--c-info)", flexShrink: 0, marginTop: 1 }}
                            />
                          )}
                          <span
                            style={{ flex: 1, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}
                          >
                            {f.title}
                          </span>
                          <span
                            className="t-mono"
                            style={{ fontSize: 10.5, color: "var(--c-ink-4)", flexShrink: 0 }}
                          >
                            {f.id}
                          </span>
                        </div>
                        <p
                          style={{
                            margin: "0 0 9px",
                            fontSize: 12,
                            lineHeight: 1.55,
                            color: "var(--c-ink-2)",
                          }}
                        >
                          {f.summary}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: 7,
                            padding: "8px 10px",
                            borderRadius: 8,
                            background: "var(--c-ai-soft)",
                            marginBottom: 10,
                          }}
                        >
                          <span
                            className="ai-badge"
                            style={{ flexShrink: 0, alignSelf: "flex-start", marginTop: 1 }}
                          >
                            <I.Sparkle size={9} /> AI
                          </span>
                          <span
                            style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--c-ink-2)" }}
                          >
                            {f.suggestion}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {f.linkedReviewId && (
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ height: 26, fontSize: 11.5 }}
                              onClick={() => onGoReview(f)}
                            >
                              <I.Arrow size={11} /> Vai a Coda revisione
                            </button>
                          )}
                          {f.pageA && (
                            <button
                              className="btn btn-sm"
                              style={{ height: 26, fontSize: 11.5 }}
                              onClick={() => onGoPages(f)}
                            >
                              <I.Network size={11} /> Vai alle pagine
                            </button>
                          )}
                          {LINT_RESOLVABLE.has(f.category) && (
                            <button
                              className="btn btn-sm"
                              style={{ height: 26, fontSize: 11.5 }}
                              onClick={() => onResolve(f)}
                            >
                              <I.Check size={11} /> Risolvi
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ height: 26, fontSize: 11.5, color: "var(--c-ink-3)" }}
                            onClick={() => onIgnore(f)}
                          >
                            Ignora
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
